import {
  FuelType,
  type Vehicle,
  VehicleCategory,
  VehicleType,
  TransmissionType,
} from "@b2bportal/ground-api";
import type { TFunction } from "i18next";

export const VehicleClass = {
  EV: "EV",
  MiniCompact: "MiniCompact",
  Standard: "Standard",
  Fullsize: "Fullsize",
  PremiumLuxury: "PremiumLuxury",
  MinivanWagon: "MinivanWagon",
  SUVSpecialty: "SUVSpecialty",
  Special: "Special",
} as const;
export type VehicleClass = keyof typeof VehicleClass;

/**
 * see https://hopper-jira.atlassian.net/wiki/x/BADEoAE
 */
export const getVehicleClass = (vehicle: Vehicle) => {
  if (vehicle.fuelType === FuelType.Electric) {
    return VehicleClass.EV;
  }
  switch (vehicle.category) {
    case VehicleCategory.Mini:
    case VehicleCategory.MiniElite:
    case VehicleCategory.Economy:
    case VehicleCategory.EconomyElite:
    case VehicleCategory.Compact:
    case VehicleCategory.CompactElite:
      return VehicleClass.MiniCompact;

    case VehicleCategory.Intermediate:
    case VehicleCategory.IntermediateElite:
    case VehicleCategory.Standard:
    case VehicleCategory.StandardElite:
      return VehicleClass.Standard;

    case VehicleCategory.Fullsize:
    case VehicleCategory.FullsizeElite:
      return VehicleClass.Fullsize;

    case VehicleCategory.Premium:
    case VehicleCategory.PremiumElite:
    case VehicleCategory.Luxury:
    case VehicleCategory.LuxuryElite:
      return VehicleClass.PremiumLuxury;

    case VehicleCategory.Oversize:
      return VehicleClass.MinivanWagon;

    case VehicleCategory.Special:
      return VehicleClass.Special;

    default:
    // try using vehicle type
  }

  switch (vehicle.vehicleType) {
    case VehicleType.WagonEstate:
    case VehicleType.PassengerVan:
    case VehicleType.PickUpSingleExtendedCar:
    case VehicleType.PickUpDoubleCab:
    case VehicleType.Crossover:
    case VehicleType.CommercialVanTruck:
      return VehicleClass.MinivanWagon;

    case VehicleType.SUV:
    case VehicleType.OpenAirAllTerrain:
      return VehicleClass.SUVSpecialty;

    case VehicleType.SpecialCar:
      return VehicleClass.Special;

    case VehicleType.SportsCar:
    case VehicleType.Convertible:
      return VehicleClass.PremiumLuxury;

    default:
      // TODO: log datadog error to track any unexpected vehicle class
      return VehicleClass.Standard;
  }
};

export const getVehicleClassLabel = (
  t: TFunction,
  vehicleClass: VehicleClass
) => {
  return {
    [VehicleClass.EV]: t("core-cars.vehicleClass.EV"),
    [VehicleClass.MiniCompact]: t("core-cars.vehicleClass.MiniCompact"),
    [VehicleClass.Standard]: t("core-cars.vehicleClass.Standard"),
    [VehicleClass.Fullsize]: t("core-cars.vehicleClass.Fullsize"),
    [VehicleClass.PremiumLuxury]: t("core-cars.vehicleClass.PremiumLuxury"),
    [VehicleClass.MinivanWagon]: t("core-cars.vehicleClass.MinivanWagon"),
    [VehicleClass.SUVSpecialty]: t("core-cars.vehicleClass.SUVSpecialty"),
    [VehicleClass.Special]: t("core-cars.vehicleClass.Special"),
  }[vehicleClass];
};

export const getVehicleClassLabelFromVehicle = (
  t: TFunction,
  vehicle: Vehicle
) => {
  const vehicleClass = getVehicleClass(vehicle);
  return getVehicleClassLabel(t, vehicleClass);
};

export const getVehicleTransmissionLabel = (
  t: TFunction,
  transmission: TransmissionType
) => {
  return {
    [TransmissionType.Automatic]: t("core-cars.transmission.Automatic"),
    [TransmissionType.Cvt]: t("core-cars.transmission.Cvt"),
    [TransmissionType.Manual]: t("core-cars.transmission.Manual"),
  }[transmission];
};

export const getBaggageQty = (vehicle: Vehicle) => {
  const values = Object.values(vehicle.bagsInfo).filter(
    (v): v is number => typeof v === "number"
  );
  return values.length > 0 ? Math.max(...values) : 0;
};
